import React, { useState } from 'react';
import moment, { Moment } from 'moment';
import styles from './custom-calendar.module.css';

interface CustomCalendarProps {
  selectedDate: Date | null;
  onDateChange: (date: Date) => void;
  onClose: () => void;
  disabled: boolean;
  minDate?: Date;
}

export const CustomCalendar: React.FC<CustomCalendarProps> = ({
  selectedDate,
  onDateChange,
  onClose,
  disabled,
  minDate,
}) => {
  const [currentDate, setCurrentDate] = useState(
    selectedDate ? moment(selectedDate) : minDate ? moment(minDate) : moment(),
  );
  const today = moment().startOf('day');
  const minDateMoment = minDate ? moment(minDate).startOf('day') : null;

  const daysInMonth = (year: number, month: number): number => {
    return moment().year(year).month(month).daysInMonth();
  };

  const handlePrevMonth = (): void => {
    if (!disabled) {
      setCurrentDate(currentDate.clone().subtract(1, 'month'));
    }
  };

  const handleNextMonth = (): void => {
    if (!disabled) {
      setCurrentDate(currentDate.clone().add(1, 'month'));
    }
  };

  const handleDateClick = (date: Moment): void => {
    if (!disabled && (!minDateMoment || date.isSameOrAfter(minDateMoment))) {
      onDateChange(date.toDate());
      onClose();
    }
  };

  const renderDays = () => {
    const totalDays = daysInMonth(currentDate.year(), currentDate.month());
    const firstDayOfMonth = moment(currentDate).startOf('month').day();
    const lastDayOfPreviousMonth = daysInMonth(currentDate.year(), currentDate.month() - 1);

    const days = [];

    for (let i = 0; i < firstDayOfMonth; i++) {
      const day = lastDayOfPreviousMonth - firstDayOfMonth + 1 + i;
      days.push(
        <div
          key={`prev-${day}`}
          className={`${styles.calendarDay} ${styles.calendarDayDisabled}`}
        >
          {day}
        </div>,
      );
    }

    for (let day = 1; day <= totalDays; day++) {
      const currentDay = moment(currentDate).date(day);

      const isSelected = selectedDate
        ? moment(selectedDate).isSame(currentDay, 'day')
        : minDate && moment(minDate).isSame(currentDay);
      const isDisabled =
        currentDay.isBefore(today, 'day') ||
        (minDateMoment && currentDay.isSameOrBefore(minDateMoment, 'day'));

      days.push(
        <div
          key={`current-${day}`}
          className={`${styles.calendarDay} ${
            isSelected ? styles.calendarDaySelected : ''
          } ${isDisabled ? styles.calendarDayDisabled : ''}`}
          onClick={!isDisabled ? () => handleDateClick(currentDay) : undefined}
        >
          {day}
        </div>,
      );
    }

    return days;
  };

  return (
    <div className={`${styles.calendarOverlay} ${disabled ? styles.calendarDisabled : ''}`}>
      <div className={styles.calendarWrapper}>
        <div className={styles.calendarHeader}>
          <div>
            <select
              value={currentDate.month()}
              disabled={disabled}
              onChange={(e) => setCurrentDate(currentDate.clone().month(parseInt(e.target.value)))}
            >
              {Array.from({ length: 12 }, (_, i) => (
                <option
                  value={i}
                  key={i}
                >
                  {moment().month(i).format('MMMM')}
                </option>
              ))}
            </select>
            <select
              value={currentDate.year()}
              disabled={disabled}
              onChange={(e) => setCurrentDate(currentDate.clone().year(parseInt(e.target.value)))}
            >
              {Array.from({ length: 5 }, (_, i) => (
                <option
                  value={moment().year() + i}
                  key={i}
                >
                  {moment().year() + i}
                </option>
              ))}
            </select>
          </div>
          <button
            type="button"
            onClick={handlePrevMonth}
            disabled={disabled}
          >
            &lt;
          </button>
          <button
            type="button"
            onClick={handleNextMonth}
            disabled={disabled}
          >
            &gt;
          </button>
        </div>
        <div className={styles.calendarGrid}>
          {['S', 'M', 'T', 'W', 'T', 'F', 'S'].map((day, i) => (
            <div
              className={styles.calendarWeekday}
              key={i}
            >
              {day}
            </div>
          ))}
          {renderDays()}
        </div>
      </div>
    </div>
  );
};
